import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle } from "@phosphor-icons/react";

const PROGRAMS = ["Infant Care", "Toddler Care", "Preschool", "Pre-K", "After School", "Full-Time", "Part-Time"];

export default function Apply() {
  const [step, setStep] = useState(1);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    child_first_name: "", child_last_name: "", child_birthday: "", program: "Preschool",
    desired_start: "", parent_name: "", parent_email: "", parent_phone: "", address: "",
    allergies: "", notes: "", signature: "",
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/applications", f);
      setDone(true);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
    setBusy(false);
  };

  if (done)
    return (
      <div className="min-h-screen grid place-items-center bg-background px-6">
        <div className="max-w-lg text-center tpl-fade-up" data-testid="apply-success">
          <CheckCircle size={72} weight="fill" className="text-[#10B981] mx-auto" />
          <h1 className="mt-8 text-4xl font-bold tracking-tight">Application received!</h1>
          <p className="mt-4 text-muted-foreground">
            Thank you, {f.parent_name}. Our team will contact you at {f.parent_email} to schedule a tour and confirm
            {" "}{f.child_first_name}'s spot. You can also reach us at (501) 904-7103.
          </p>
          <div className="mt-9 flex flex-wrap gap-4 justify-center">
            <Link to="/"><Button variant="outline" className="rounded-full h-12 px-7 font-bold" data-testid="apply-home-btn">Back to site</Button></Link>
            <Link to="/register">
              <Button className="rounded-full h-12 px-7 bg-[#3B82F6] hover:bg-[#2563EB] font-bold" data-testid="apply-register-btn">
                Create family account
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-background px-6 sm:px-10 py-14">
      <div className="mx-auto max-w-2xl">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold text-muted-foreground hover:text-[#3B82F6]"
              style={{ transition: "color .2s ease" }} data-testid="apply-back-link">
          <ArrowLeft size={16} /> Back to site
        </Link>
        <p className="mt-8 text-sm uppercase tracking-[0.2em] font-bold text-[#3B82F6]">Enrollment Application</p>
        <h1 className="mt-4 text-4xl sm:text-5xl font-bold tracking-tight">Let's get your child enrolled.</h1>
        <div className="mt-8 flex gap-2" data-testid="apply-progress">
          {[1, 2, 3].map((s) => (
            <span key={s} className={`h-2 flex-1 rounded-full ${step >= s ? "bg-[#3B82F6]" : "bg-border"}`}
                  style={{ transition: "background-color .3s ease" }} />
          ))}
        </div>

        <form onSubmit={submit} className="mt-10 bg-white rounded-3xl border border-border p-8 space-y-6 shadow-sm"
              data-testid="apply-form">
          {step === 1 && (
            <div className="space-y-5" data-testid="apply-step-1">
              <h2 className="font-display text-2xl font-semibold">About your child</h2>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="font-bold text-sm">Child first name</Label>
                  <Input required value={f.child_first_name} onChange={set("child_first_name")}
                         className="mt-2 h-12 rounded-xl" data-testid="apply-child-first" />
                </div>
                <div>
                  <Label className="font-bold text-sm">Child last name</Label>
                  <Input required value={f.child_last_name} onChange={set("child_last_name")}
                         className="mt-2 h-12 rounded-xl" data-testid="apply-child-last" />
                </div>
                <div>
                  <Label className="font-bold text-sm">Date of birth</Label>
                  <Input type="date" value={f.child_birthday} onChange={set("child_birthday")}
                         className="mt-2 h-12 rounded-xl" data-testid="apply-child-dob" />
                </div>
                <div>
                  <Label className="font-bold text-sm">Desired start date</Label>
                  <Input type="date" value={f.desired_start} onChange={set("desired_start")}
                         className="mt-2 h-12 rounded-xl" data-testid="apply-start-date" />
                </div>
              </div>
              <div>
                <Label className="font-bold text-sm">Program</Label>
                <Select value={f.program} onValueChange={(v) => setF({ ...f, program: v })}>
                  <SelectTrigger className="mt-2 h-12 rounded-xl" data-testid="apply-program"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PROGRAMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button type="button" onClick={() => setStep(2)}
                      className="w-full h-12 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold" data-testid="apply-next-1">
                Continue
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5" data-testid="apply-step-2">
              <h2 className="font-display text-2xl font-semibold">Parent / guardian</h2>
              <div>
                <Label className="font-bold text-sm">Your full name</Label>
                <Input required value={f.parent_name} onChange={set("parent_name")}
                       className="mt-2 h-12 rounded-xl" data-testid="apply-parent-name" />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="font-bold text-sm">Email</Label>
                  <Input type="email" required value={f.parent_email} onChange={set("parent_email")}
                         className="mt-2 h-12 rounded-xl" data-testid="apply-parent-email" />
                </div>
                <div>
                  <Label className="font-bold text-sm">Phone</Label>
                  <Input value={f.parent_phone} onChange={set("parent_phone")}
                         className="mt-2 h-12 rounded-xl" data-testid="apply-parent-phone" />
                </div>
              </div>
              <div>
                <Label className="font-bold text-sm">Home address</Label>
                <Input value={f.address} onChange={set("address")} className="mt-2 h-12 rounded-xl" data-testid="apply-address" />
              </div>
              <div className="flex gap-4">
                <Button type="button" variant="outline" onClick={() => setStep(1)}
                        className="h-12 rounded-full px-7 font-bold" data-testid="apply-back-1">Back</Button>
                <Button type="button" onClick={() => setStep(3)}
                        className="flex-1 h-12 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold" data-testid="apply-next-2">
                  Continue
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5" data-testid="apply-step-3">
              <h2 className="font-display text-2xl font-semibold">Health & signature</h2>
              <div>
                <Label className="font-bold text-sm">Allergies or food sensitivities</Label>
                <Input value={f.allergies} onChange={set("allergies")} className="mt-2 h-12 rounded-xl" data-testid="apply-allergies" />
              </div>
              <div>
                <Label className="font-bold text-sm">Anything else we should know?</Label>
                <Textarea value={f.notes} onChange={set("notes")} className="mt-2 rounded-xl min-h-24" data-testid="apply-notes" />
              </div>
              <div>
                <Label className="font-bold text-sm">Type your full name to sign</Label>
                <Input required value={f.signature} onChange={set("signature")}
                       className="mt-2 h-14 rounded-xl font-display text-xl" placeholder="Your signature"
                       data-testid="apply-signature" />
                <p className="mt-2 text-xs text-muted-foreground">
                  By signing you confirm the information above is accurate and agree to our enrollment policies.
                </p>
              </div>
              <div className="flex gap-4">
                <Button type="button" variant="outline" onClick={() => setStep(2)}
                        className="h-12 rounded-full px-7 font-bold" data-testid="apply-back-2">Back</Button>
                <Button type="submit" disabled={busy}
                        className="flex-1 h-12 rounded-full bg-[#FBBF24] hover:bg-[#F59E0B] text-[#0F172A] font-bold"
                        data-testid="apply-submit">
                  {busy ? "Submitting…" : "Submit application"}
                </Button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
